import { CORE_SETUP_LABELS } from '../../app/terminology/setup';
import { getComponentLabel } from '../../app/terminology/components';
import type { SetupHealth } from '../../app/models/SetupHealth';
import { getThemeActivationState, type ThemeActivationStatus } from './themeActivationStatus';

type SetupStep = { label: string; status: 'complete' | 'action' | 'unknown' };
export type DashboardSetupStep = SetupStep & {
    id: string;
    completedLabel: string;
    url: string;
    external?: boolean;
};

// Keep these aligned with the setup checklist and the API's required core checks.
export const getCoreSetupSteps = ({ core }: SetupHealth): SetupStep[] => [
    { label: CORE_SETUP_LABELS.fields, status: core.enabledFieldCount > 0 ? 'complete' : 'action' },
    { label: CORE_SETUP_LABELS.records, status: core.fitmentRecordCount > 0 || core.referenceDataEnabled ? 'complete' : 'action' },
    { label: CORE_SETUP_LABELS.products, status: core.productFitmentAssignmentCount > 0 || core.externalProductLinkCount > 0 || core.universalProductCount > 0 ? 'complete' : 'action' },
    { label: CORE_SETUP_LABELS.storefront, status: core.storefrontConfigured && core.storefrontEnabled ? 'complete' : 'action' },
];

export function getDashboardSetupStatus(health: SetupHealth, activations: ThemeActivationStatus) {
    const core = getCoreSetupSteps(health);
    const destinations = [
        { id: 'fields', completedLabel: 'Selector fields configured', url: '/storefront?section=selector-fields' },
        { id: 'records', completedLabel: 'Fitment records available', url: health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled ? '/vehicles' : '/imports' },
        { id: 'products', completedLabel: 'Products connected to fitments', url: '/products' },
        { id: 'storefront', completedLabel: 'Storefront enabled', url: '/storefront?section=search-results' },
    ];
    const steps: DashboardSetupStep[] = core.map((step, index) => ({ ...step, ...destinations[index] }));
    // A check that couldn't run adds no step: it must not block or pass setup.
    const scriptTags = health.legacyScriptTags?.status;
    if (scriptTags === 'Found' || scriptTags === 'None') {
        steps.push({
            id: 'legacy-script-tags',
            label: 'Replace legacy ScriptTags before March 1, 2027',
            completedLabel: 'No legacy ScriptTags',
            url: '/setup-health',
            status: scriptTags === 'Found' ? 'action' : 'complete',
        });
    }
    // The endpoint defaults to the published theme, but can fall back to a draft
    // when no published theme exists. Never treat that draft as live-store health.
    if (!health.themeInspectionAvailable || !health.selectedTheme?.isPublished) {
        steps.push({ id: 'theme', label: 'Verify published theme installation', completedLabel: 'Published theme verified', status: 'unknown', url: '/setup-health' });
    } else {
        steps.push({
            id: 'theme', completedLabel: 'Theme compatible', url: '/setup-health',
            label: 'Review published theme compatibility',
            status: health.selectedTheme.supportsJsonTemplates ? 'complete' : 'action',
        });
    }
    // Backend file inspection remains useful for compatibility, but only App
    // Bridge determines whether this app's blocks are active on the live theme.
    for (const component of health.components.filter(item => item.required && item.applicable
        && (item.key !== 'product-results' || health.renderingMode === 'RenderedResults'))) {
        const activation = getThemeActivationState(activations, component.handle);
        const label = getComponentLabel(component.key, component.label);
        // Deep-link only into a compatible published theme. Otherwise the setup
        // page explains theme selection/manual installation before adding blocks.
        const themeUrl = health.themeInspectionAvailable && health.selectedTheme?.isPublished
            && health.selectedTheme.supportsJsonTemplates
            ? activation === 'available' ? component.actionUrl : component.manageUrl
            : '';
        steps.push({
            id: component.key,
            completedLabel: `${label} active`,
            url: themeUrl || '/setup-health',
            external: !!themeUrl,
            label: activation === 'unavailable'
                ? `Review ${label} availability in your published theme`
                : `Add ${label} to your published theme`,
            status: activation === 'active' ? 'complete' : activation === 'unknown' ? 'unknown' : 'action',
        });
    }
    const pending = steps.filter(step => step.status === 'action');
    const unverified = steps.some(step => step.status === 'unknown');
    return {
        steps,
        pending,
        unverified,
        complete: steps.filter(step => step.status === 'complete').length,
        total: steps.length,
        coreComplete: core.every(step => step.status === 'complete'),
        ready: pending.length === 0 && !unverified,
    };
}
